import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { Nullable } from '~types/Nullable';

export interface QuickBooksOption {
  id: string;
  name: string;
}

interface MappingSelectFormValues {
  item: Nullable<QuickBooksOption>;
}

interface MappingSelectProps {
  dataTestId: string;
  disabled?: boolean;
  onSave: (option: Nullable<QuickBooksOption>) => Promise<boolean>;
  options: QuickBooksOption[];
  placeholder?: string;
  value: Nullable<QuickBooksOption>;
  // required: a `100%` select collapses inside DataTable's shrink-to-fit cells
  width: number;
}

export default function MappingSelect({
  dataTestId,
  disabled = false,
  onSave,
  options,
  placeholder,
  value,
  width,
}: MappingSelectProps) {
  const [isSaving, setIsSaving] = useState(false);
  const {
    control,
    setValue,
    formState: { errors },
  } = useForm<MappingSelectFormValues>({ defaultValues: { item: value } });
  const selectedOption = useWatch({ control, name: 'item' });

  // a failed save refetches the dataset, so the row's value prop is the source of
  // truth the cell-local form must follow
  useEffect(() => {
    setValue('item', value);
  }, [value?.id, setValue]);

  const handleSelect = async (option: Nullable<QuickBooksOption>) => {
    setIsSaving(true);

    try {
      const saved = await onSave(option ?? null);

      // a failed save can leave the row's authoritative value unchanged (so the
      // value-prop sync effect never fires); reset the optimistic selection here
      if (!saved) {
        setValue('item', value);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      data-test-id={dataTestId}
      // MUI DataGrid consumes the space key for row selection, so typing a space in
      // the cell-local input only works if propagation stops before the grid sees it
      onKeyDown={(event) => {
        if (event.key === ' ') {
          event.stopPropagation();
        }
      }}
      // a long selected name is clipped by the input, so hovering shows it in full
      title={selectedOption?.name || undefined}
      sx={{ display: 'flex', width }}
    >
      <AutocompleteFormField
        clearable
        control={control}
        disablePortal={false}
        disabled={isSaving || disabled}
        errors={errors}
        getLabel={(option: Nullable<QuickBooksOption>) => option?.name || ''}
        getValue={(option: Nullable<QuickBooksOption>) => option?.id || ''}
        list={options}
        name="item"
        onSelect={(option: Nullable<QuickBooksOption>) => {
          void handleSelect(option);
        }}
        sx={{ width: '100%' }}
        placeholder={
          placeholder ??
          t(
            'administration.integration.rutter.quick_books_online.tread_first.select_from_quick_books',
          )
        }
      />
    </Box>
  );
}
